import { Box, Tooltip } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  EyeIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  XCircleIcon,
  UserCircleIcon,
  ExclamationCircleIcon,
  ArrowRightIcon
} from "@heroicons/react/24/outline";
import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Loader from "../../../components/Loader";

const getStatusInfo = (statut) => {
  switch (statut) {
    case "Client":
      return { color: "bg-green-100 text-green-700", icon: <CheckCircleIcon className="w-5 h-5 text-green-500" /> };
    case "Sans Suite":
      return { color: "bg-red-100 text-red-700", icon: <XCircleIcon className="w-5 h-5 text-red-500" /> };
    case "Prospect Qualifié":
      return { color: "bg-blue-100 text-blue-700", icon: <UserCircleIcon className="w-5 h-5 text-blue-500" /> };
    case "Prospect":
      return { color: "bg-yellow-100 text-yellow-700", icon: <ExclamationCircleIcon className="w-5 h-5 text-yellow-500" /> };
    default:
      return { color: "bg-gray-100 text-gray-700", icon: <UserCircleIcon className="w-5 h-5 text-gray-500" /> };
  }
};

const BACKEND_URL =
    window.location.hostname === "localhost"
      ? process.env.REACT_APP_BACKEND_URL_LOCAL + "/api"
      : process.env.REACT_APP_BACKEND_URL_PROD + "/api";

function CustomNoRowsOverlay() {
  const navigate = useNavigate();
  const [showLoader, setShowLoader] = useState(true);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setShowLoader(false);
    }, 50000);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="flex flex-col items-center justify-center h-[280px] w-full space-y-6">
      {showLoader ? (
        <Loader />
      ) : (
        <>
          <p className="text-gray-600 text-sm">Aucun prospect trouvé.</p>
          <button
            onClick={() => navigate("/prospects/leads")}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition font-semibold"
          >
            <ArrowRightIcon className="w-5 h-5" />
            Aller à la page Leads
          </button>
        </>
      )}
    </div>
  );
}

export default function ProspectsTable({ prospects, onDelete, selectProspect, onEdit, refreshProspects }) {

  const handleArchive = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${BACKEND_URL}/prospects/${id}`,
        { statut: "Sans Suite" },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (refreshProspects) refreshProspects();
    } catch (error) {
      console.error("Erreur lors de l'archivage du prospect :", error);
    }
  };

  const columns = [
    { field: "type", headerName: "Type", width: 150, headerAlign: "center", align: "center" },
    { field: "nom", headerName: "Nom", width: 150, headerAlign: "center", align: "center" },
    { field: "prenom", headerName: "Prénom", width: 150, headerAlign: "center", align: "center" },
    { field: "email", headerName: "Email", width: 230, headerAlign: "center", align: "center" },
    { field: "telephone", headerName: "Téléphone", width: 150, headerAlign: "center", align: "center" },
    { field: "adresse", headerName: "Adresse", width: 150, headerAlign: "center", align: "center" },
    { field: "commentaire", headerName: "Commentaires", width: 200, headerAlign: "center", align: "center" },
    {
      field: "statut",
      headerName: "Statut",
      width: 160,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => {
        const { color, icon } = getStatusInfo(params.value);
        return (
          <div className="flex items-center justify-center w-full h-full">
            <div className={`flex items-center gap-2 ${color} rounded-full px-3 py-1 text-sm font-semibold`}>
              {icon}
              {params.value}
            </div>
          </div>
        );
      },
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 200,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <div className="flex items-center justify-center w-full h-full">
          <div className="flex gap-2">
            <Tooltip title="Voir la fiche">
              <button
                className="bg-blue-600 hover:bg-blue-700 text-white p-2 rounded-full transition"
                onClick={() => selectProspect(params.row)}
              >
                <EyeIcon className="w-4 h-4" />
              </button>
            </Tooltip>

            <Tooltip title="Modifier">
              <button
                className="bg-yellow-500 hover:bg-yellow-600 text-white p-2 rounded-full transition"
                onClick={() => onEdit(params.row)}
              >
                <PencilIcon className="w-4 h-4" />
              </button>
            </Tooltip>

            <Tooltip title="Archiver">
              <button
                className="bg-red-600 hover:bg-red-700 text-white p-2 rounded-full transition"
                onClick={() => handleArchive(params.row._id)}
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </Tooltip>
          </div>
        </div>
      ),
    },
  ];

  return (
    <Box sx={{ height: 400, width: "100%" }}>
      <DataGrid
        rows={prospects}
        columns={columns}
        getRowId={(row) => row._id}
        checkboxSelection
        onRowClick={(params) => selectProspect(params.row)}
        slots={{ noRowsOverlay: CustomNoRowsOverlay }}
      />
    </Box>
  );
}
